import React, { useEffect, useState } from 'react';
import { campaignsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Plus, Calendar, Download, Lock, Unlock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function Campaigns() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, title: '', description: '', deadline_days: 25 });

  const load = () => {
    setLoading(true);
    campaignsApi.list().then(r => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const createCamp = async () => {
    try {
      await campaignsApi.create({ ...form, year: parseInt(form.year), month: parseInt(form.month) });
      toast.success('Campaña creada');
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear'); }
  };

  const toggle = async (c) => {
    try {
      if (c.status === 'abierta') await campaignsApi.close(c.id);
      else await campaignsApi.reopen(c.id);
      toast.success('Estado actualizado');
      load();
    } catch { toast.error('Error'); }
  };

  const remove = async (c) => {
    const msg = `¿Eliminar la campaña "${c.period_label}"?\n\nSe borrarán también todos los datos y envíos asociados. Esta acción no se puede deshacer.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await campaignsApi.remove(c.id);
      toast.success(`Eliminada · ${r.data.deleted_datapoints} datos y ${r.data.deleted_submissions} envíos borrados`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  const template = async (c) => {
    try {
      const r = await campaignsApi.downloadTemplate(c.id);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `AEA_plantilla_${c.period_label.replace(/ /g, '_')}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-4 animate-fade-in-up">
      <Card className="aea-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-aea-blue" /> Campañas mensuales</CardTitle>
            <p className="text-sm text-slate-500 mt-1">Cada mes se abre una campaña de recopilación de datos.</p>
          </div>
          {isAdmin && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button className="bg-aea-blue hover:bg-aea-blue-dk" data-testid="btn-new-campaign"><Plus className="w-4 h-4 mr-2" /> Nueva campaña</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nueva campaña mensual</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Año</Label><Input type="number" value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} data-testid="camp-year" /></div>
                    <div>
                      <Label>Mes</Label>
                      <Select value={String(form.month)} onValueChange={(v) => setForm({ ...form, month: v })}>
                        <SelectTrigger data-testid="camp-month"><SelectValue /></SelectTrigger>
                        <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Título (opcional)</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="camp-title" /></div>
                  <div><Label>Descripción (opcional)</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="camp-desc" /></div>
                  <div><Label>Días para subir datos</Label><Input type="number" value={form.deadline_days} onChange={e => setForm({ ...form, deadline_days: parseInt(e.target.value) })} data-testid="camp-deadline" /></div>
                  <Button className="w-full bg-aea-blue hover:bg-aea-blue-dk" onClick={createCamp} data-testid="btn-create-campaign">Crear campaña</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-aea-blue" /></div> :
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3">Periodo</th>
                  <th className="text-left p-3">Título</th>
                  <th className="text-left p-3">Estado</th>
                  <th className="text-right p-3">Envíos / Empresas</th>
                  <th className="text-left p-3">Fecha límite</th>
                  <th className="text-right p-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Sin campañas creadas</td></tr>}
                {items.map((c) => (
                  <tr key={c.id} className="border-t" data-testid={`camp-row-${c.id}`}>
                    <td className="p-3 font-medium">{c.period_label}</td>
                    <td className="p-3 text-slate-600">{c.title}</td>
                    <td className="p-3"><span className={`status-badge status-${c.status === 'abierta' ? 'open' : 'closed'}`}>{c.status}</span></td>
                    <td className="p-3 text-right font-mono">{c.total_submissions} / {c.total_companies}</td>
                    <td className="p-3 text-xs text-slate-500 font-mono">{new Date(c.deadline).toLocaleDateString('es-ES')}</td>
                    <td className="p-3 text-right space-x-2">
                      <Button size="sm" variant="outline" onClick={() => template(c)} data-testid={`btn-template-${c.id}`}><Download className="w-3.5 h-3.5 mr-1" /> Plantilla</Button>
                      {isAdmin && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => toggle(c)} data-testid={`btn-toggle-${c.id}`}>
                            {c.status === 'abierta' ? <><Lock className="w-3.5 h-3.5 mr-1" /> Cerrar</> : <><Unlock className="w-3.5 h-3.5 mr-1" /> Reabrir</>}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(c)}
                            data-testid={`btn-del-camp-${c.id}`}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </CardContent>
      </Card>
    </div>
  );
}
